import {
  ace,
  breakPointsWon,
  courtAd,
  doubleFault,
  firstServe,
  pointCategoryUnforced,
  pointCategoryWinner,
  receivingPointsWon,
  scoreDictionary,
  statisticTypeName,
  totalPointWon,
  unforcedDoubleFault,
  unforcedErrors,
  winnerAce,
  winners,
  winOnFirstServe,
  winOnSecondServe,
} from "constants/tennis";
import moment from "moment";

export function getPlayerInitials(player) {
  return `${player.firstName[0]}${player.lastName[0] ?? ""}`.toUpperCase();
}

export function getPlayerName(player) {
  return `${player.firstName}`.toUpperCase();
}

export function getLastSet(tennisMatch) {
  if (!tennisMatch.sets) return;
  return tennisMatch.sets[tennisMatch.sets.length - 1];
}

export function getLastGame(tennisMatch) {
  const set = getLastSet(tennisMatch);
  if (!set) return;
  return set.games[set.games.length - 1];
}

export function getLastPoint(tennisMatch) {
  const game = getLastGame(tennisMatch);
  if (!game) return;
  return game.points[game.points.length - 1];
}

export function getMatchGraph(match, stopAtPoint) {
  var matchGraph = new MatchGraph();
  matchGraph.sets = [];
  matchGraph.duration = 0;
  var stop = false;
  var setCount = 0;

  var previousPoint;

  for (const setKey in match.sets) {
    if (Object.hasOwnProperty.call(match.sets, setKey)) {
      const s = match.sets[setKey];
      if (stop) break;
      setCount++;
      var _set = new SetGraph();
      _set.index = setCount;
      _set.duration = 0;
      _set.t1SetScore = 0;
      _set.t2SetScore = 0;
      _set.winnerTeamID = s.winnerTeamID;
      _set.games = [];

      for (var gameKey in s.games) {
        if (Object.hasOwnProperty.call(s.games, gameKey)) {
          const g = s.games[gameKey];
          if (stop) break;
          var _game = new GameGraph();
          _game.t1GameScore = 0;
          _game.t2GameScore = 0;
          _game.t1SetScore = _set.t1SetScore;
          _game.t2SetScore = _set.t2SetScore;
          _game.team1Serving = g.servingTeamID === 1;
          _game.duration = 0;
          _game.tieBreak = g.tieBreak;
          _game.points = [];

          for (var pointKey in g.points) {
            if (Object.hasOwnProperty.call(g.points, pointKey)) {
              const p = g.points[pointKey];
              var _point = new PointGraph();
              _point.winningPoint = false;
              _point.t1PointScore = 0;
              _point.t2PointScore = 0;
              _point.point = p;

              if (previousPoint != null) {
                _point.duration = moment
                  .duration(moment(p.date).diff(moment(previousPoint.date)))
                  .asSeconds();
              } else {
                _point.duration = moment
                  .duration(moment(p.date).diff(moment(match.date)))
                  .asSeconds();
              }
              _game.duration += _point.duration;

              if (p.winnerTeamID === 1) {
                _game.t1GameScore++;
              } else {
                _game.t2GameScore++;
              }

              _point.t1PointScore = _game.t1GameScore;
              _point.t2PointScore = _game.t2GameScore;
              _point.winnerTeamID = p.winnerTeamID;

              _point.winningPoint =
                p.id === g.points[g.points.length - 1].id && g.winnerTeamID > 0;
              _game.points.push(_point);

              if (_point.winningPoint) {
                if (g.winnerTeamID === 1) _game.t1SetScore++;
                if (g.winnerTeamID === 2) _game.t2SetScore++;
              }

              previousPoint = p;
              _set.t1SetScore = _game.t1SetScore;
              _set.t2SetScore = _game.t2SetScore;

              if (stopAtPoint != null && stopAtPoint.id === p.id) {
                stop = true;
                break;
              }
            }
          }

          _game.winnerTeamID = _game.t1GameScore > _game.t2GameScore ? 1 : 2;
          _set.duration += _game.duration;
          _set.games.push(_game);
        }
      }

      matchGraph.duration += _set.duration;
      matchGraph.sets.push(_set);
    }
  }

  return matchGraph;
}

class MatchGraph {
  constructor(sets, duration, team1Name, team2Name) {
    this.sets = sets;
    this.duration = duration;
    this.team1Name = team1Name;
    this.team2Name = team2Name;
  }
}

class SetGraph {
  constructor(
    index,
    games,
    duration,
    winnerTeamID,
    icon,
    t1SetScore,
    t2SetScore
  ) {
    this.index = index;
    this.games = games;
    this.duration = duration;
    this.winnerTeamID = winnerTeamID;
    this.icon = icon;
    this.t1SetScore = t1SetScore;
    this.t2SetScore = t2SetScore;
  }
}

class GameGraph {
  constructor(
    points,
    duration,
    winnerTeamID,
    t1GameScore,
    t2GameScore,
    t1SetScore,
    t2SetScore,
    team1Serving,
    tieBreak
  ) {
    this.points = points;
    this.duration = duration;
    this.winnerTeamID = winnerTeamID;
    this.t1GameScore = t1GameScore;
    this.t2GameScore = t2GameScore;
    this.t1SetScore = t1SetScore;
    this.t2SetScore = t2SetScore;
    this.team1Serving = team1Serving;
    this.tieBreak = tieBreak;
  }
}

class PointGraph {
  constructor(
    t1PointScore,
    t2PointScore,
    duration,
    winnerTeamID,
    winningPoint,
    icon,
    point,
    team1Serving,
    tieBreak
  ) {
    this.t1PointScore = t1PointScore;
    this.t2PointScore = t2PointScore;
    this.duration = duration;
    this.winnerTeamID = winnerTeamID;
    this.winningPoint = winningPoint;
    this.icon = icon;
    this.point = point;
  }
}

export function printDuration(seconds) {
  var date = new Date(0);
  date.setSeconds(seconds); // specify value for SECONDS here
  var timeString = date.toISOString().substr(14, 5);
  return timeString;
}

export function winningTeamID(score1, score2) {
  if (score1 <= 3 && score2 <= 3) return -1;
  if (score1 - score2 >= 2) return 1;
  if (score2 - score1 >= 2) return 2;

  return -1;
}

export function scoreLabels(score1, score2, tieBreak) {
  var scoreFourty = scoreDictionary[3].toString();
  var scoreTeam1 = score1.toString();
  var scoreTeam2 = score2.toString();

  if (tieBreak) {
    return [scoreTeam1, scoreTeam2];
  }

  if (score1 >= 3 && score2 >= 3) {
    if (score1 === score2) {
      scoreTeam1 = scoreFourty;
      scoreTeam2 = scoreFourty;
    } else if (score1 > score2) {
      scoreTeam1 = courtAd.toUpperCase();
      scoreTeam2 = scoreFourty;
    } else if (score1 < score2) {
      scoreTeam1 = scoreFourty;
      scoreTeam2 = courtAd.toUpperCase();
    }
  } else {
    scoreTeam1 = score1 > 3 ? "Ga" : scoreDictionary[score1].toString();
    scoreTeam2 = score2 > 3 ? "Ga" : scoreDictionary[score2].toString();
  }

  return [scoreTeam1, scoreTeam2];
}

export function capitalizeFirstLetter(string) {
  return (
    string.toLowerCase().charAt(0).toUpperCase() + string.toLowerCase().slice(1)
  );
}

export function getLabelForTeam(players, teamID) {
  if (players.length === 2) {
    return capitalizeFirstLetter(
      getPlayerName(teamID === 1 ? players[0] : players[1])
    );
  }

  return `${capitalizeFirstLetter(
    getPlayerName(teamID === 1 ? players[0] : players[1])
  )} & ${capitalizeFirstLetter(
    getPlayerName(teamID === 1 ? players[2] : players[3])
  )}`;
}

export function getInitialForTeam(players, teamID) {
  if (players.length === 2) {
    return getPlayerInitials(teamID === 1 ? players[0] : players[1]);
  }

  return `${getPlayerInitials(
    teamID === 1 ? players[0] : players[1]
  )} & ${getPlayerInitials(teamID === 1 ? players[2] : players[3])}`;
}

export function getAllPoints(tennisMatch) {
  const points = [];
  for (const setKey in tennisMatch.sets) {
    if (Object.hasOwnProperty.call(tennisMatch.sets, setKey)) {
      const set = tennisMatch.sets[setKey];
      for (const gameKey in set.games) {
        if (Object.hasOwnProperty.call(set.games, gameKey)) {
          const game = set.games[gameKey];
          points.push(...game.points);
        }
      }
    }
  }
  return points;
}

// STATS
const evaluationForType = {
  ace: (p, teamID) => p.outcome === winnerAce,
  doubleFault: (p, teamID) => p.outcome === unforcedDoubleFault,
  firstServe: (p, teamID) => p.serveCount === 0,
  winOnFirstServe: (p, teamID) => p.serveCount === 0,
  winOnSecondServe: (p, teamID) => p.serveCount === 1,
  receivingPointsWon: (p, teamID) => p.servingTeamID !== teamID,
  winners: (p, teamID) => p.winnerTeamID === teamID,
  unforcedErrors: (p, teamID) => p.winnerTeamID === teamID,
  totalPointWon: (p, teamID) => true,
};

const selectionForType = {
  ace: (p, teamID) => p.winnerTeamID === teamID,
  doubleFault: (p, teamID) => p.winnerTeamID !== teamID,
  firstServe: (p, teamID) => p.servingTeamID === teamID,
  winOnFirstServe: (p, teamID) => p.winnerTeamID === teamID,
  winOnSecondServe: (p, teamID) => p.winnerTeamID === teamID,
  receivingPointsWon: (p, teamID) => p.winnerTeamID === teamID,
  winners: (p, teamID) => p.category === pointCategoryWinner,
  unforcedErrors: (p, teamID) => p.category === pointCategoryUnforced,
  totalPointWon: (p, teamID) => p.winnerTeamID === teamID,
};
export function getValueCountForType(type, _points, teamIndex) {
  var res = new IntTuple();

  if (evaluationForType[type] != null && selectionForType[type] != null) {
    return getValueCountForEvaluation(
      _points,
      teamIndex,
      evaluationForType[type],
      selectionForType[type]
    );
  }

  const val = 0;
  res.count = 0;
  res.value = val;

  return res;
}

export function getTeamStatistic(forType, _points, teamIndex) {
  var teamStat = {};

  var _tuple = getValueCountForType(forType, _points, teamIndex);

  teamStat.count = _tuple.count ?? 0;
  teamStat.value = _tuple.value ?? 0;
  teamStat.teamIndex = teamIndex;
  teamStat.type = forType;
  teamStat.name = localStatisticTypeName(forType);

  return teamStat;
}

export function getMatchStatData(tennisMatch) {
  const _stats = [];
  var _points = getAllPoints(tennisMatch);

  for (const key in statisticTypeName) {
    if (Object.hasOwnProperty.call(statisticTypeName, key)) {
      const type = statisticTypeName[key];
      _stats.push({
        1: getTeamStatistic(type, _points, 1),
        2: getTeamStatistic(type, _points, 2),
      });
    }
  }

  return _stats;
}

export function getValueCountForEvaluation(
  _points,
  teamIndex,
  evaluation,
  selection
) {
  var _totalPoint = _points.filter((element) => evaluation(element, teamIndex));
  var _subSelection = _totalPoint.filter((element) =>
    selection(element, teamIndex)
  );

  return new IntTuple(_totalPoint.length, _subSelection.length);
}

class IntTuple {
  constructor(value, count) {
    this.value = value;
    this.count = count;
  }
}

function localStatisticTypeName(statisticName) {
  switch (statisticName) {
    case ace:
      return "ACES";

    case doubleFault:
      return "DOUBLE FAULTS";

    case firstServe:
      return "FIRST SERVE";

    case winOnFirstServe:
      return "WIN ON FIRST SERVE";

    case winOnSecondServe:
      return "WIN ON SECOND SERVE";

    case breakPointsWon:
      return "BREAK POINT WON";

    case receivingPointsWon:
      return "RECEIVING POINTS WON";

    case winners:
      return "WINNERS";

    case unforcedErrors:
      return "UNFORCED ERRORS";

    case totalPointWon:
      return "TOTAL POINT WON";

    default:
      return "";
  }
}

// The display pair for a game's current score.
//
// Prefers scoreMap (the point counts) over the pre-rendered `score` string:
// old app builds wrote strings like "null-30" into that field, and those
// documents are already in Firestore, so the string cannot be trusted. The
// counts in scoreMap were correct even in those matches.
export function gameScoreLabels(game) {
  if (!game) return [];

  const map = game.scoreMap;
  if (map) {
    const t1 = Number(map[1] ?? map["1"] ?? 0);
    const t2 = Number(map[2] ?? map["2"] ?? 0);
    if (Number.isFinite(t1) && Number.isFinite(t2)) {
      return scoreLabels(t1, t2, !!game.tieBreak);
    }
  }

  // No scoreMap at all: fall back to the string, but never render "null".
  const parts = `${game.score ?? ""}`.split("-");
  if (parts.length === 2 && !parts.some((p) => p === "null" || p === "")) {
    return parts;
  }
  return [];
}

// The two teams, in the app's order (ExtTennisMatch.getTeams): in doubles the
// players array interleaves the sides, so team 1 is 0 and 2, team 2 is 1 and 3.
export function getTeams(players) {
  const p = players || [];
  const teams =
    p.length === 4
      ? [
          [p[0], p[2]],
          [p[1], p[3]],
        ]
      : [[p[0]], [p[1]]];
  return teams.map((team) => team.filter(Boolean));
}

// Point counts for a game, from scoreMap (see gameScoreLabels for why the
// pre-rendered `score` string can't be trusted).
function gameScoreCounts(game) {
  const map = (game && game.scoreMap) || {};
  const t1 = Number(map[1] ?? map["1"] ?? 0);
  const t2 = Number(map[2] ?? map["2"] ?? 0);
  return [Number.isFinite(t1) ? t1 : 0, Number.isFinite(t2) ? t2 : 0];
}

// One side's label on the score card, mirroring the app's getScoreDisplay.
//
// Not the same as scoreLabels: each side gets its OWN tile there, so at
// advantage the leading side reads "AD" and the trailing side a dash, where a
// single combined line would read "AD - 40".
function gameTileLabel(mine, theirs, tieBreak) {
  if (tieBreak) return `${mine}`;

  if (mine >= 3 && theirs >= 3) {
    if (mine === theirs) return scoreDictionary[3].toString();
    return mine - theirs === 1 ? courtAd.toUpperCase() : "-";
  }

  // Past 40 without the other side at 40 the game is already won; only a
  // malformed document gets here, and "Ga" is what the rest of the widget
  // shows for it.
  return scoreDictionary[mine] !== undefined
    ? scoreDictionary[mine].toString()
    : "Ga";
}

export function gameTileLabels(game) {
  if (!game) return ["", ""];
  const [t1, t2] = gameScoreCounts(game);
  const tieBreak = !!game.tieBreak;
  return [gameTileLabel(t1, t2, tieBreak), gameTileLabel(t2, t1, tieBreak)];
}

// One column per set on the score card, mirroring
// ExtTennisSet.getScoreForBoard: games won by each team, and — once a set that
// ended in a tiebreak is complete — the tiebreak points as an exponent.
//
// Counts the games rather than reading set.scoreMap, as the app does: the
// per-game winnerTeamID is the value the app itself derives from.
export function setScoresForBoard(tennisMatch) {
  const sets = (tennisMatch && tennisMatch.sets) || [];

  return sets.map((set) => {
    const games = set.games || [];
    let scoreTeam1 = 0;
    let scoreTeam2 = 0;
    for (const game of games) {
      if (game.winnerTeamID === 1) scoreTeam1++;
      else if (game.winnerTeamID === 2) scoreTeam2++;
    }

    const completed = (set.winnerTeamID ?? -1) > 0;
    const lastGame = games[games.length - 1];
    let exponentTeam1 = "";
    let exponentTeam2 = "";
    if (completed && lastGame && lastGame.tieBreak) {
      const [t1, t2] = gameScoreCounts(lastGame);
      exponentTeam1 = `${t1}`;
      exponentTeam2 = `${t2}`;
    }

    return {
      scoreTeam1: `${scoreTeam1}`,
      scoreTeam2: `${scoreTeam2}`,
      completed,
      winnerTeamID: set.winnerTeamID || 0,
      exponentTeam1,
      exponentTeam2,
    };
  });
}
